import {
  Button,
  Card,
  Inline,
  Input,
  Spinner,
  Stack,
  Message
} from "@adfenix/alexandria";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext
} from "react-hook-form";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAgentById,
  useGetImageTypeSettings,
  useUpdateAgentById
} from "api/queries/agentService";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { AgentService } from "@adfenix/types";
import { useRecoilValue } from "recoil";
import { userSelectedHq } from "atoms/auth";
import Popover from "components/Popover";
import { SolidCircleQuestion } from "@adfenix/icons";
import { GenericError } from "components/GenericError";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Sentry from "lib/sentry/init";
import { useTranslation } from "react-i18next";
import Divider from "components/Divider";
import { ImageUpload } from "./ImageUpload";
import { OfficeSelection } from "./OfficeSelection";
import { Coordinates } from "./Coordinates";
import { useUploadedImage } from "api/services/mediaService";
import { AgentServiceApi, QuedroIntegrationServiceApi } from "api/services";
import useUserInfo from "hooks/useUserInfo";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";

const ProfileCard = styled(Card)`
  margin-top: 16px;
  max-width: 450px;
`;

const AdditionalImageUpload = ({
  image
}: {
  image: AgentService.AgentImageTypeModel;
}) => {
  const { control } = useFormContext<AgentService.AgentModel>();

  const {
    append,
    update,
    fields: additionalImages
  } = useFieldArray({
    name: "additionalImages",
    control
  });

  const uploadedInfo = additionalImages?.find(x => x.name === image.name);
  const { data, isFetching } = useUploadedImage(uploadedInfo?.url, true);

  return (
    <ImageUpload
      isFetching={isFetching}
      name={image.displayName}
      image={data?.uploadedUrl}
      setImage={url => {
        if (uploadedInfo) {
          let index = 0;
          for (let n = 0; n < additionalImages.length; n++) {
            if (additionalImages[n].name === image.name) {
              index = n;
              break;
            }
          }
          update(index, {
            url,
            name: image.name,
            displayName: image.displayName
          });
        } else {
          append({ name: image.name, url, displayName: image.displayName });
        }
      }}
    />
  );
};

export const AgentProfile = () => {
  const { t } = useTranslation();
  const { hqId } = useParams<HqIdRouteParams>();
  const { agentId } = useParams<{ agentId: string }>();
  const selectedHq = useRecoilValue(userSelectedHq);
  const { data: imageTypeSettings } = useGetImageTypeSettings(selectedHq?.id);
  const methods = useForm<AgentService.AgentModel>({
    reValidateMode: "onChange"
  });

  const {
    register,
    reset,
    watch,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid }
  } = methods;

  const {
    data: agentData,
    isInitialLoading,
    isError,
    isFetching
  } = useGetAgentById(agentId);

  const queryClient = useQueryClient();
  const image = watch("image");

  const {
    agent: {
      authV1AgentEnableAutomaticUpdatesUpdate,
      authV1AgentDeactivateCreate
    }
  } = AgentServiceApi;

  const {
    contact: { authV1ContactAgentUpdateDetail }
  } = QuedroIntegrationServiceApi;

  const { isLoading: isSyncronizingContacts, mutateAsync: syncContacts } =
    useMutation(({ hqId, agentId }: { hqId: string; agentId: string }) =>
      authV1ContactAgentUpdateDetail(hqId, agentId)
    );

  const {
    isLoading: isEnablingAutomaticUpdates,
    mutateAsync: updateAutomaticUpdates
  } = useMutation((id: string) => authV1AgentEnableAutomaticUpdatesUpdate(id));

  const { isLoading: isArchivingAgent, mutateAsync: archiveAgent } =
    useMutation((id: string) => authV1AgentDeactivateCreate(id));

  const { isLoading: isUpdatingAgent, mutateAsync: updateAgent } =
    useUpdateAgentById();
  useEffect(() => {
    reset(agentData);
  }, [agentData, reset]);

  const navigate = useNavigate();
  const { accessLevel } = useUserInfo();

  if (!hqId) {
    return <Message type="warning">No HQ!</Message>;
  }

  if (!agentId) {
    return <Message type="warning">{t("agentprofile.missing.id")}</Message>;
  }

  if (isError) {
    return <GenericError />;
  }

  if (isInitialLoading || !agentData) {
    return <Spinner centered />;
  }

  return (
    <Page header={<PageHeader title={t("agentprofile.my.profile")} />}>
      <ProfileCard>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(async values => {
              try {
                values.primaryHqId = agentData.primaryHqId;
                await updateAgent(values);
                reset(values);
                toast.success(t("agentprofile.successful.update"));
                queryClient.invalidateQueries(["getAgentById", agentId]);
              } catch (e) {
                Sentry.captureException(e);
                toast.error(t("agentprofile.agentupdate.failure"));
              }
            })}
          >
            <Stack spacing="m">
              <ImageUpload
                isFetching={false}
                name={t("agentprofile.agentimage")}
                image={image}
                setImage={url => {
                  setValue("image", url, {
                    shouldDirty: true
                  });
                }}
              />

              {imageTypeSettings &&
                imageTypeSettings.map(x => (
                  <AdditionalImageUpload key={x.name} image={x} />
                ))}

              <Input
                label={t("agentprofile.name")}
                {...register("name", { required: true })}
              />
              <Input
                disabled
                type="email"
                label={t("agentprofile.email")}
                {...register("emailAddress")}
              />

              <Input
                type="url"
                label={t("results.customizableLandingPage.label")}
                {...register("landingPage")}
              />

              <Input label={t("agentprofile.title")} {...register("title")} />

              <Input
                type="tel"
                label={t("agentprofile.phone")}
                {...register("phoneNumber")}
              />

              <Input
                label={t("agentprofile.licensenumber")}
                {...register("licenseNumber")}
              />

              <OfficeSelection agentData={agentData} />

              <Coordinates agentData={agentData} />

              <Button
                logLabel="update-agent-button"
                isLoading={isFetching || isUpdatingAgent}
                type="submit"
                disabled={!isDirty || !isValid}
                style={{ marginLeft: "auto" }}
              >
                {t("agentprofile.update")}
              </Button>

              <Divider margin="24px 0" />

              <Button
                logLabel="sync-agent-data-button"
                isLoading={isSyncronizingContacts}
                variant="secondary"
                onClick={async () => {
                  try {
                    await syncContacts({ hqId, agentId });
                    toast.success("Agents contacts succesfully synced");
                    queryClient.invalidateQueries(["getAgentById"]);
                  } catch (e) {
                    toast.error(t("Error syncing agent contacts"));
                  }
                }}
              >
                Sync data
              </Button>
              <Inline justifyContent="space-between" alignItems="center">
                <Button
                  logLabel="change-automatic-updates-button"
                  isLoading={isEnablingAutomaticUpdates}
                  disabled={
                    isUpdatingAgent || agentData.automaticUpdatesEnabled
                  }
                  variant="secondary"
                  onClick={async () => {
                    try {
                      await updateAutomaticUpdates(agentData.id);
                      toast.success(t("agentprofile.successful.update"));
                      queryClient.invalidateQueries(["getAgentById"]);
                    } catch (e) {
                      toast.error(t("agentprofile.agentupdate.failure"));
                    }
                  }}
                >
                  {t("agentprofile.enable.automatic.updates")}
                </Button>

                <Popover trigger={<SolidCircleQuestion />}>
                  <>{t("agentprofile.automatic.updates.description")}</>
                </Popover>
              </Inline>

              {accessLevel === "Multi-Brand" && (
                <Button
                  isLoading={isArchivingAgent}
                  logLabel="archive-agent-button"
                  variant="danger"
                  onClick={async () => {
                    try {
                      await archiveAgent(agentId);
                      toast.success(
                        "Agent archived. There might be a delay before the agent is removed from the list."
                      );
                      navigate("/back-office/agent-profiles");
                    } catch (e) {
                      toast.error("Failed to archive agent");
                    }
                  }}
                >
                  {t("agentprofile.archive")}
                </Button>
              )}
            </Stack>
          </form>
        </FormProvider>
      </ProfileCard>
    </Page>
  );
};
